import { useCallback, useEffect } from 'react';
import { PageView, UserInteractionEvent } from '../../models/analytics';
import { useAnalytics } from './AnalyticsProvider';
import {
  InteractionElement,
  ParentMessenger,
  parentMessenger,
} from './ParentApplicationEventMessenger';

type UsePageViewEventsPayload = {
  pageView: PageView;
  messenger?: ParentMessenger;
};

export const usePageViewEvents = ({
  pageView,
  messenger = parentMessenger,
}: UsePageViewEventsPayload) => {
  const { tinklinkAnalytics: analytics } = useAnalytics();
  const sendViewInteractionEvent = useCallback(
    ({
      interaction,
      element,
      label,
    }: {
      interaction: UserInteractionEvent;
      element?: InteractionElement;
      label?: string;
    }) => {
      analytics.sendInteractionEvent({
        action: `${pageView}/${interaction}`,
        view: pageView,
        label,
      });

      if (element)
        messenger?.fireInteractionEvent({
          pageView,
          element,
        });
    },
    [analytics, messenger, pageView]
  );

  /**
   * Sending events once tracks usage of specific functionality
   * that should be of no interest for a parent frame. Hence there should be
   * no `parentMessenger.fireInteractionEvent` to accompany this event.
   */
  const sendViewInteractionEventOnce = useCallback(
    ({ interaction, label }: { interaction: UserInteractionEvent; label?: string }) => {
      analytics.sendInteractionEventOnce({
        action: `${pageView}/${interaction}`,
        view: pageView,
        label,
      });
    },
    [analytics, pageView]
  );

  const sendEvent = useCallback(
    (view: PageView) => {
      analytics.sendViewEvent({ view });
      messenger.fireViewEvent(view);
    },
    [analytics, messenger]
  );

  useEffect(() => {
    sendEvent(pageView);
  }, [pageView, sendEvent]);

  return {
    analytics,
    sendEvent,
    sendViewInteractionEvent,
    sendViewInteractionEventOnce,
  };
};
